import React from 'react';
import { Check, Minus, Wrench } from 'lucide-react';
import usePlanComparison from '../../../hooks/usePlanComparison';

/**
 * GOV13-A6 — authoritative, catalogue-driven per-plan comparison. Every inclusion/limit/integration state is
 * looked up from the server projection (GET /billing/plans/comparison); this component decides NO commercial
 * truth. Annual price shows only when the server supplies an authoritative value (never fabricated/derived).
 * Plan-specific optional add-ons are DEFERRED (GOV13-A3) and shown as a truthful note, not a purchase action.
 */
const CATEGORY_LABELS = {
  core: 'Core Platform', ai: 'AI & Automation', supervisor: 'Live Supervision', workflow: 'Workflow & Scheduling',
  knowledge: 'Knowledge', engagement: 'Engagement', analytics: 'Analytics & Reporting', growth: 'Growth',
  platform: 'Platform', enterprise: 'Enterprise', security: 'Security', compliance: 'Compliance', vertical: 'Industry',
  other: 'Other',
};
const CATEGORY_ORDER = ['core', 'ai', 'workflow', 'supervisor', 'knowledge', 'analytics', 'engagement',
  'growth', 'security', 'compliance', 'platform', 'enterprise', 'vertical', 'other'];
const LIMIT_ROWS = [
  ['max_staff', 'Staff seats'], ['monthly_engagements', 'Monthly engagements'],
  ['active_integrations', 'Active integrations'], ['monthly_ai_resolutions', 'Monthly AI resolutions'],
  ['storage_gb', 'Storage'],
];

const priceLabel = (cents) => (cents == null ? null : `$${Math.round(cents / 100).toLocaleString()}`);
const limitLabel = (key, v) => {
  if (v === -1) return 'Unlimited';
  if (v === null || v === undefined) return 'Not specified';
  if (key === 'storage_gb') return v >= 1024 ? `${Math.round(v / 1024)} TB` : `${v} GB`;
  return Number(v).toLocaleString();
};
const featState = (plan, key) => (plan.included_feature_keys.includes(key) ? 'INCLUDED' : 'NOT_INCLUDED');
const connState = (plan, key) => (!plan.included_connector_keys.includes(key)
  ? 'NOT_INCLUDED' : (plan.setup_required_connector_keys.includes(key) ? 'SETUP_REQUIRED' : 'INCLUDED'));

function StateCell({ state }) {
  if (state === 'INCLUDED') return <Check className="mx-auto h-4 w-4 text-emerald-600" aria-label="Included" />;
  if (state === 'SETUP_REQUIRED') {
    return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700"><Wrench className="h-3 w-3" />Setup</span>;
  }
  return <Minus className="mx-auto h-4 w-4 text-gray-300" aria-label="Not included" />;
}

export function PlanComparison() {
  const { data, loading, error } = usePlanComparison();
  if (loading) return <div className="py-6 text-center text-sm text-gray-500">Loading full plan comparison…</div>;
  if (error || !data || !(data.plans || []).length) return null; // honest: no invented data

  const plans = data.plans;
  const byCat = {};
  (data.capability_catalog || []).forEach((c) => { (byCat[c.category] || (byCat[c.category] = [])).push(c); });
  const cats = CATEGORY_ORDER.filter((c) => byCat[c] && byCat[c].length);

  const Header = () => (
    <thead>
      <tr>
        <th className="sticky left-0 z-10 bg-white p-3 text-left text-sm font-semibold text-gray-900">Plan</th>
        {plans.map((p) => (
          <th key={p.plan_key} className="min-w-[8rem] p-3 text-center align-top">
            <div className="text-sm font-semibold text-gray-900">{p.display_name}{p.popular && <span className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">Popular</span>}</div>
            <div className="mt-1 text-lg font-bold text-gray-900">
              {p.on_request ? 'Custom' : <>{priceLabel(p.monthly_price_cents)}<span className="text-xs font-normal text-gray-500">/mo</span></>}
            </div>
            <div className="text-[11px] text-gray-500">
              {p.annual_price_cents != null ? `${priceLabel(p.annual_price_cents)}/yr` : (p.on_request ? 'Contact sales' : 'Annual pricing not currently available')}
            </div>
            {p.trial_days ? <div className="text-[11px] text-emerald-700">{p.trial_days}-day trial</div> : null}
          </th>
        ))}
      </tr>
    </thead>
  );

  const Section = ({ title, children }) => (
    <>
      <tr className="bg-gray-50"><td className="sticky left-0 bg-gray-50 p-2 text-xs font-semibold uppercase tracking-wide text-gray-500" colSpan={plans.length + 1}>{title}</td></tr>
      {children}
    </>
  );
  const Row = ({ label, cells }) => (
    <tr className="border-t border-gray-100">
      <td className="sticky left-0 z-10 bg-white p-2.5 text-sm text-gray-700">{label}</td>
      {cells}
    </tr>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white" data-testid="plan-comparison">
      <div className="border-b border-gray-100 p-4">
        <h4 className="text-lg font-semibold text-gray-900">Compare what each plan includes</h4>
        <p className="text-xs text-gray-500">Exact per-plan inclusion, limits and integrations — sourced from the live plan catalogue.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <Header />
          <tbody>
            {cats.map((cat) => (
              <Section key={cat} title={CATEGORY_LABELS[cat] || cat}>
                {byCat[cat].map((cap) => (
                  <Row key={cap.key} label={cap.name}
                    cells={plans.map((p) => <td key={p.plan_key} className="p-2.5 text-center"><StateCell state={featState(p, cap.key)} /></td>)} />
                ))}
              </Section>
            ))}

            {(data.integration_catalog || []).length > 0 && (
              <Section title="Integrations">
                {data.integration_catalog.map((ig) => (
                  <Row key={ig.key} label={ig.name}
                    cells={plans.map((p) => <td key={p.plan_key} className="p-2.5 text-center"><StateCell state={connState(p, ig.key)} /></td>)} />
                ))}
              </Section>
            )}

            <Section title="Limits">
              {LIMIT_ROWS.map(([key, label]) => (
                <Row key={key} label={label}
                  cells={plans.map((p) => <td key={p.plan_key} className="p-2.5 text-center text-sm text-gray-800">{limitLabel(key, p.limits ? p.limits[key] : null)}</td>)} />
              ))}
            </Section>
          </tbody>
        </table>
      </div>
      <div className="border-t border-gray-100 p-3 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> Included</span>
        <span className="ml-3 inline-flex items-center gap-1"><Wrench className="h-3 w-3 text-amber-700" /> Setup required</span>
        <span className="ml-3 inline-flex items-center gap-1"><Minus className="h-3 w-3 text-gray-300" /> Not included</span>
        <span className="ml-3">· Additional add-ons and packs may be available after signup.</span>
      </div>
    </div>
  );
}

export default PlanComparison;
